export const SENSITIVE_ANALYTICS_QUERY_PARAMS = Object.freeze([
  "access_token",
  "api_key",
  "apikey",
  "auth",
  "authorization",
  "code",
  "email",
  "id_token",
  "password",
  "qac_token",
  "qac_recipient",
  "refresh_token",
  "secret",
  "session",
  "state",
  "token",
]);

export const PRIVATE_MEDIA_URL_REDACTION = "[REDACTED_PRIVATE_MEDIA_URL]";
export const LINKEDIN_REPORT_TOKEN_REDACTION = "[REDACTED_REPORT_TOKEN]";

const LINKEDIN_REPORT_PATH_PATTERN = /(\/linkedin\/reporte\/)[^/?#\s)]+/gi;

const PRIVATE_MEDIA_TEXT_MARKER =
  /(?:[?&;\s(])(x-amz-(?:signature|credential|security-token|algorithm)|x-goog-(?:signature|credential|algorithm)|awsaccesskeyid|key-pair-id|googleaccessid|sig|signature)=/i;

const SENSITIVE_QUERY_PARAM_NAMES = new Set(
  SENSITIVE_ANALYTICS_QUERY_PARAMS.map((name) => name.toLowerCase()),
);

const SIGNED_QUERY_PARAM_PREFIXES = ["x-amz-", "x-goog-", "cloudfront-"];

const SIGNATURE_QUERY_PARAM_NAMES = new Set([
  "awsaccesskeyid",
  "credential",
  "googleaccessid",
  "key-pair-id",
  "key_pair_id",
  "security-token",
  "security_token",
  "sig",
  "signature",
]);

const SIGNED_URL_COMPANION_PARAM_NAMES = new Set([
  "algorithm",
  "date",
  "expires",
  "key-pair-id",
  "key_pair_id",
  "policy",
  "se",
  "sip",
  "sp",
  "spr",
  "sr",
  "srt",
  "st",
  "sv",
  "signedheaders",
]);

function normalizeQueryParamName(name) {
  return typeof name === "string" ? name.trim().toLowerCase() : "";
}

function hasSignedPrefix(name) {
  return SIGNED_QUERY_PARAM_PREFIXES.some((prefix) => name.startsWith(prefix));
}

export function isSensitiveAnalyticsQueryParam(name, signedUrl = false) {
  const normalizedName = normalizeQueryParamName(name);
  if (!normalizedName) return false;

  return (
    SENSITIVE_QUERY_PARAM_NAMES.has(normalizedName) ||
    SIGNATURE_QUERY_PARAM_NAMES.has(normalizedName) ||
    hasSignedPrefix(normalizedName) ||
    (signedUrl && SIGNED_URL_COMPANION_PARAM_NAMES.has(normalizedName))
  );
}

function hasSigningParameters(searchParams) {
  return Array.from(searchParams.keys()).some((name) => {
    const normalizedName = normalizeQueryParamName(name);
    return (
      SIGNATURE_QUERY_PARAM_NAMES.has(normalizedName) ||
      hasSignedPrefix(normalizedName)
    );
  });
}

function hasPrivateDraftPath(url) {
  try {
    return decodeURIComponent(url.pathname).toLowerCase().includes("/_drafts/");
  } catch {
    return url.pathname.toLowerCase().includes("/_drafts/");
  }
}

function decodedForPrivateMediaInspection(value) {
  let decoded = String(value || "");
  for (let pass = 0; pass < 2; pass += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }
  return decoded;
}

export function sanitizeAnalyticsText(value) {
  if (typeof value !== "string" || !value) return value;
  const inspected = decodedForPrivateMediaInspection(value);
  if (
    inspected.toLowerCase().includes("/_drafts/") ||
    PRIVATE_MEDIA_TEXT_MARKER.test(inspected)
  ) {
    return PRIVATE_MEDIA_URL_REDACTION;
  }
  return value.replace(
    LINKEDIN_REPORT_PATH_PATTERN,
    `$1${LINKEDIN_REPORT_TOKEN_REDACTION}`,
  );
}

export function sanitizeAnalyticsUrl(
  value,
  baseUrl = "https://analytics.invalid",
) {
  if (typeof value !== "string" || !value) return value;

  try {
    const url = new URL(value, baseUrl);
    const signedUrl = hasSigningParameters(url.searchParams);
    if (signedUrl || hasPrivateDraftPath(url)) {
      return PRIVATE_MEDIA_URL_REDACTION;
    }
    url.pathname = url.pathname.replace(
      LINKEDIN_REPORT_PATH_PATTERN,
      `$1${LINKEDIN_REPORT_TOKEN_REDACTION}`,
    );
    const queryParamNames = Array.from(new Set(url.searchParams.keys()));

    for (const name of queryParamNames) {
      if (isSensitiveAnalyticsQueryParam(name, signedUrl)) {
        url.searchParams.set(name, "[REDACTED]");
      }
    }

    return url.toString();
  } catch {
    // Invalid URLs are left untouched; callers should only pass URL fields.
    return value;
  }
}

export function sanitizeAnalyticsNetworkRequest(
  request,
  baseUrl = "https://analytics.invalid",
) {
  if (!request || typeof request !== "object") return request;

  const sanitizedRequest = { ...request };
  if (typeof sanitizedRequest.name === "string") {
    sanitizedRequest.name = sanitizeAnalyticsUrl(
      sanitizedRequest.name,
      baseUrl,
    );
  }
  delete sanitizedRequest.requestBody;
  delete sanitizedRequest.responseBody;
  delete sanitizedRequest.requestHeaders;
  delete sanitizedRequest.responseHeaders;
  return sanitizedRequest;
}

export function sanitizeAnalyticsEventProperties(event, sanitize) {
  if (!event?.properties) return event;
  // rrweb masks inputs/private surfaces before encoding. Treat its compressed
  // snapshot payload as opaque: text/property scrubbing can corrupt the codec.
  const hasSnapshot = event.event === "$snapshot" &&
    Object.hasOwn(event.properties, "$snapshot_data");
  const metadata = { ...event.properties };
  if (hasSnapshot) delete metadata.$snapshot_data;
  const properties = sanitize(metadata, { preservePostHogInternals: true });
  if (hasSnapshot) properties.$snapshot_data = event.properties.$snapshot_data;
  return { ...event, properties };
}
